/**
 * WebSocket service for Gaia Assistant custom protocol.
 *
 * Client → Agent: SEND_MESSAGE, USER_STATE_SNAPSHOT, TOOL_CONFIRMATION_RESPONSE, REQUEST_MESSAGE_HISTORY_SNAPSHOT
 * Agent → Client: TEXT_START, TEXT_DELTA, TEXT_END, REQUEST_USER_STATE_SNAPSHOT, TOOL_CONFIRMATION_REQUEST, MESSAGE_HISTORY_SNAPSHOT
 */

import { getAuthToken } from "features/auth/utils";
import { GaiaConstants } from "utils";
import { MessageRole } from "features/assistant/definitions";

export interface TextStartEvent {
  type: "TEXT_START";
  message_id: string;
}

export interface TextDeltaEvent {
  type: "TEXT_DELTA";
  message_id: string;
  delta: string;
}

export interface TextEndEvent {
  type: "TEXT_END";
  message_id: string;
}

export interface ToolConfirmationRequestEvent {
  type: "TOOL_CONFIRMATION_REQUEST";
  request_id: string;
  tool_name: string;
  description: string;
}

export interface RequestUserStateSnapshotEvent {
  type: "REQUEST_USER_STATE_SNAPSHOT";
}

export interface HistoryContentBlock {
  type: "text" | "tool_use";
  text?: string;
  display_text?: string;
  tool_name?: string;
  description?: string;
  status?: "completed" | "denied" | "error";
}

export interface HistoryMessage {
  role: MessageRole;
  content: HistoryContentBlock[];
}

export interface SessionMetadata {
  feedback?: "THUMBS_UP" | "THUMBS_DOWN" | null;
}

export interface MessageHistorySnapshotEvent {
  type: "MESSAGE_HISTORY_SNAPSHOT";
  messages: HistoryMessage[];
  session_metadata?: SessionMetadata;
}

export interface ToolCallStartEvent {
  type: "TOOL_CALL_START";
  message_id: string;
  tool_use_id: string;
  tool_name: string;
}

export interface ToolCallEndEvent {
  type: "TOOL_CALL_END";
  message_id: string;
  tool_use_id: string;
  tool_name: string;
  status: "completed" | "error";
  result: string;
}

export interface ErrorEvent {
  type: "ERROR";
  message: string;
}

export type ServerEvent =
  | TextStartEvent
  | TextDeltaEvent
  | TextEndEvent
  | ToolCallStartEvent
  | ToolCallEndEvent
  | ToolConfirmationRequestEvent
  | RequestUserStateSnapshotEvent
  | MessageHistorySnapshotEvent
  | ErrorEvent;

export type ServerEventHandler = (event: ServerEvent) => void;

export class AssistantWebSocket {
  private ws: WebSocket | null = null;
  private onEvent: ServerEventHandler;
  private onConnectionChange: (connected: boolean) => void;

  constructor(
    onEvent: ServerEventHandler,
    onConnectionChange: (connected: boolean) => void,
  ) {
    this.onEvent = onEvent;
    this.onConnectionChange = onConnectionChange;
  }

  connect(url: string): void {
    this.disconnect();

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log("Assistant WebSocket connected:", url);
        this.onConnectionChange(true);
        this.sendUserStateSnapshot();
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const parsed = JSON.parse(event.data) as ServerEvent;

          if (parsed.type === "REQUEST_USER_STATE_SNAPSHOT") {
            this.sendUserStateSnapshot();
            return;
          }

          this.onEvent(parsed);
        } catch {
          console.error("Failed to parse server event:", event.data);
        }
      };

      this.ws.onclose = (event: CloseEvent) => {
        console.log(
          `Assistant WebSocket closed: code=${event.code} reason="${event.reason}" wasClean=${event.wasClean}`,
        );
        this.onConnectionChange(false);
      };

      this.ws.onerror = (event: Event) => {
        console.error("WebSocket error:", event);
      };
    } catch (err) {
      console.error("Failed to create WebSocket:", err);
      this.onConnectionChange(false);
    }
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
  }

  sendMessage(prompt: string, displayText?: string): void {
    this.send({
      type: "SEND_MESSAGE",
      prompt,
      ...(displayText && { display_text: displayText }),
    });
  }

  async sendUserStateSnapshot(): Promise<void> {
    const jwtToken = await getAuthToken();
    const teamName =
      localStorage.getItem(GaiaConstants.SELECTED_TEAM_NAME) || "";
    const timezoneCode = Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.send({
      type: "USER_STATE_SNAPSHOT",
      state: {
        jwt_token: jwtToken,
        current_team_name: teamName,
        timezone_code: timezoneCode,
      },
    });
  }

  sendToolConfirmationResponse(requestId: string, confirmed: boolean): void {
    this.send({
      type: "TOOL_CONFIRMATION_RESPONSE",
      request_id: requestId,
      confirmed,
    });
  }

  sendRequestMessageHistory(): void {
    this.send({ type: "REQUEST_MESSAGE_HISTORY_SNAPSHOT" });
  }

  sendInterruptResponse(): void {
    this.send({ type: "INTERRUPT_RESPONSE" });
  }

  sendFeedback(feedback: "THUMBS_UP" | "THUMBS_DOWN"): void {
    this.send({ type: "SUBMIT_FEEDBACK", feedback });
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private send(data: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }
}
